import { type ComponentConfig } from "@puckeditor/core";
import {
  getWindowJobsiteConfig,
  type JobsiteBusinessContext,
  type JobsiteBusinessServiceValue,
} from "../../types/runtime-config";
import { resolveBusinessCtaText } from "../../lib/business-bound-prop";
import { resolvesToPhoneUrl } from "../../lib/cta-defaults";
import { ContactForm, type ContactFormProps, type FormTypePreset } from "./contact-form";
import { ContactFormFields } from "./contact-form.fields";
import { RegenerableTextField } from "../editor/RegenerableTextField";

// Default headings based on form type
const getDefaultHeading = (formType: FormTypePreset, businessName?: string): string => {
  switch (formType) {
    case "quick-quote":
      return "Get a Free Quote";
    case "schedule":
      return "Schedule Your Service";
    case "emergency":
      return "Need Emergency Service?";
    case "contact":
    default:
      return businessName ? `Contact ${businessName}` : "Get In Touch";
  }
};

// Default subheadings based on form type
const getDefaultSubheading = (formType: FormTypePreset, businessPhone?: string): string => {
  switch (formType) {
    case "quick-quote":
      return "Tell us about your project and we'll get back to you with a free estimate.";
    case "schedule":
      return "Choose a time that works for you and we'll confirm your appointment.";
    case "emergency":
      return businessPhone
        ? `Call ${businessPhone} for immediate assistance or submit below.`
        : "We respond to emergencies 24/7. Submit your request now.";
    case "contact":
    default:
      return businessPhone
        ? `Call us at ${businessPhone} or fill out the form below`
        : "Fill out the form below and we'll get back to you as soon as possible";
  }
};

// The builder-only prop (preview) stays out of the Puck generic: Puck's
// slot-type transform mangles nested Data-shaped props (puck#1519), and a
// jobsite page never sets it — only the form builder and stories do.
type ContactFormPuckProps = Omit<ContactFormProps, "preview">;

export const ContactFormConfig: ComponentConfig<ContactFormPuckProps> = {
  label: "Contact Form",
  fields: {
    formType: ContactFormFields.formType,
    heading: {
      ...ContactFormFields.heading,
      contentEditable: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: ({ value, onChange, field }: any) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.contactform.heading"
          label="Heading"
          multiline={false}
          fieldMeta={field?.metadata}
        />
      ),
    },
    subheading: {
      ...ContactFormFields.subheading,
      contentEditable: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: ({ value, onChange, field }: any) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.contactform.subheading"
          label="Subheading"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    variant: ContactFormFields.variant,
    showUrgencySelector: ContactFormFields.showUrgencySelector,
    layout: ContactFormFields.layout,
    submitButtonText: {
      ...ContactFormFields.submitButtonText,
      contentEditable: true,
    },
    successMessage: ContactFormFields.successMessage,
    backgroundColor: ContactFormFields.backgroundColor,
  },
  defaultProps: {
    formType: "contact",
    heading: "", // Let render handle the default based on formType
    subheading: "", // Let render handle the default based on formType
    variant: "card",
    showUrgencySelector: "no",
    layout: "centered",
    submitButtonText: "", // Let component use formType default
    successMessage: "Thank you! We'll be in touch soon.",
  },
  render: ({ id, puck, ...props }) => {
    // Access business data from Puck metadata OR window config (fallback)
    const puckMetadata = puck?.metadata as { business?: JobsiteBusinessContext } | undefined;
    const windowConfig = getWindowJobsiteConfig();
    const business = puckMetadata?.business || windowConfig?.business;

    // Get phone number - check both 'phone' and 'telephone' property names
    const businessPhone = business?.phone || business?.telephone;
    const businessEmail = business?.email;
    const businessUrl = business?.website || business?.url || windowConfig?.siteUrl;

    // Get services from business context for dropdown
    const serviceOptions =
      business?.services?.map((service: JobsiteBusinessServiceValue) =>
        typeof service === "string" ? service : service.name || "",
      ) || [];

    // Resolve form type (default to contact)
    const formType = props.formType || "contact";

    // Determine hours available string (24/7 for emergency services)
    const hoursAvailable = business?.is24Hours || formType === "emergency" ? "24/7" : undefined;

    // Submit button: owner Puck edit (props.submitButtonText) wins; otherwise
    // when the business has a Site-level CTA, use it. When neither, leave
    // undefined so the formType-based preset default kicks in
    // (`getDefaultSubmitText` in contact-form.tsx). Tolerate a ReactNode:
    // the editors swap the string for an inline-editable element (the field
    // is contentEditable), and an element means the owner is editing it, so
    // it wins like any owner-set value. Strings keep the exact old semantics.
    const submitButtonText =
      typeof props.submitButtonText === "string" || props.submitButtonText == null
        ? props.submitButtonText?.trim()
          ? props.submitButtonText
          : // A phone-posture site CTA ("Call Now") is a lie on a submit
            // button — pressing it submits a form. Only borrow the site CTA
            // when it describes a form action; else the formType default wins.
            business?.ctaPrimaryLabel?.trim() &&
              !resolvesToPhoneUrl(resolveBusinessCtaText(undefined, business))
            ? resolveBusinessCtaText(undefined, business)
            : undefined
        : props.submitButtonText;

    // Default values if not provided in props
    const resolvedProps = {
      ...props,
      formType,
      heading: props.heading || getDefaultHeading(formType, business?.name),
      subheading: props.subheading || getDefaultSubheading(formType, businessPhone),
      submitButtonText,
      variant: props.variant || "card",
      serviceOptions,
      // Schema props from business context
      businessName: business?.name,
      businessPhone,
      businessEmail,
      businessUrl,
      hasTrackingPhone: !!(business as JobsiteBusinessContext | undefined)?.hasTrackingPhone,
      hoursAvailable,
      // Same canonical slug the booking widget uses — photo uploads resolve on it.
      businessSlug: business?.slug,
    };

    return <ContactForm id={id} {...resolvedProps} />;
  },
};
